import { useEffect, useState } from "react";
import { Stack, CheckCircle, WarningCircle, MinusCircle, ArrowsClockwise, Airplane } from "@phosphor-icons/react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/auth";
import { timeAgo, fmtAUD } from "../lib/format";
import { toast } from "sonner";

export default function DataSources() {
  const [data, setData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [scrapers, setScrapers] = useState([]);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();

  const load = async () => {
    const r = await api.get("/data-sources/status");
    setData(r.data);
    const j = await api.get("/data-sources/jobs");
    setJobs(j.data);
    const s = await api.get("/scrapers/status");
    setScrapers(s.data);
  };

  useEffect(() => { load(); }, []);

  const refresh = async (useSerpapi, useTier1 = false) => {
    setBusy(true);
    try {
      await api.post(`/data-sources/refresh?use_serpapi=${useSerpapi}&use_tier1=${useTier1}`);
      toast.success("Refresh started");
      setTimeout(load, 4000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const canRefresh = user?.role === "admin" || user?.role === "analyst";

  const statusIcon = (s) =>
    s === "ok" ? <CheckCircle weight="fill" color="#10B981" size={18} />
      : s === "stubbed" ? <MinusCircle weight="duotone" color="#8DA8D6" size={18} />
      : <WarningCircle weight="fill" color="#F59E0B" size={18} />;

  return (
    <div className="p-6 lg:p-10 max-w-[1600px]">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em]" style={{ color: "var(--saa-gold)" }}>
            <Stack size={14} weight="duotone" /> Pipeline
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2" data-testid="ds-title">Data Sources</h1>
          <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>
            5-tier cascading fetch — each tier fills airlines the previous tier missed.
          </p>
        </div>
        {canRefresh && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => refresh(false, false)} disabled={busy} data-testid="ds-refresh-mock">
              <ArrowsClockwise size={14} className="mr-1" /> Refresh (mock)
            </Button>
            <Button variant="outline" onClick={() => refresh(true, false)} disabled={busy} data-testid="ds-refresh-serpapi">
              SerpAPI only
            </Button>
            <Button onClick={() => refresh(true, true)} disabled={busy} data-testid="ds-refresh-full"
                    style={{ background: "var(--saa-gold)", color: "#0A1428" }}>
              Full live (SerpAPI + Tier 1)
            </Button>
          </div>
        )}
      </div>

      {/* Airline scraper health */}
      <div className="saa-card p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Airplane size={16} weight="duotone" color="var(--saa-gold)" />
          <h2 className="font-display text-lg font-semibold">Airline-direct scrapers (Tier 1)</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {scrapers.map((s) => (
            <div key={s.iata} className="p-3 rounded-lg" style={{ background: "var(--card-bg-2)", border: "1px solid var(--border-subtle)" }}
                 data-testid={`scraper-${s.iata}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-sm">{s.iata}</span>
                  <span className="text-xs" style={{ color: "var(--text-secondary)" }}>{s.name}</span>
                </div>
                {s.enabled ? (
                  s.success_rate_pct == null ? (
                    <span className="pill pill-no-data">Untested</span>
                  ) : s.success_rate_pct > 50 ? (
                    <span className="pill pill-competitive">{s.success_rate_pct}%</span>
                  ) : s.success_rate_pct > 0 ? (
                    <span className="pill pill-slight">{s.success_rate_pct}%</span>
                  ) : (
                    <span className="pill pill-undercut">0%</span>
                  )
                ) : (
                  <span className="pill pill-no-data">Stub</span>
                )}
              </div>
              <div className="text-[11px] space-y-0.5" style={{ color: "var(--text-secondary)" }}>
                <div>Attempts: <span className="font-mono" style={{ color: "var(--text-primary)" }}>{s.attempts}</span></div>
                <div>Successes: <span className="font-mono" style={{ color: "var(--text-primary)" }}>{s.successes}</span></div>
                {s.last_success_fare && (
                  <div>Last fare: <span className="font-mono" style={{ color: "var(--saa-gold)" }}>{fmtAUD(s.last_success_fare)}</span></div>
                )}
                {s.last_success_at && <div>Last OK: {timeAgo(s.last_success_at)}</div>}
                {s.last_error && (
                  <div className="mt-1 italic truncate" style={{ color: "var(--warning)" }} title={s.last_error}>
                    {s.last_error.slice(0, 60)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 text-[11px]" style={{ color: "var(--text-muted)" }}>
          Tier-1 scraping is best-effort against each airline's public site via Zyte browser rendering.
          Stubs (EK/SQ/QR/CX/VA/ET) cascade cleanly to SerpAPI. Use the per-cell "🔍 verify" button on the
          Route Detail matrix to spot-check any specific cell on demand.
        </div>
      </div>

      {/* Tier diagram */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 mb-6">
        {(data?.tiers || []).map((t) => (
          <div key={t.tier} className="saa-card p-4">
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs px-2 py-0.5 rounded"
                    style={{ background: "var(--card-bg-2)", color: "var(--saa-gold)" }}>
                T{t.tier}
              </span>
              {statusIcon(t.status)}
            </div>
            <div className="mt-2 font-display font-semibold text-sm leading-tight">{t.name}</div>
            <div className="mt-2 text-[11px]" style={{ color: "var(--text-secondary)" }}>
              {t.configured ? "Configured" : "Not configured"}
              {t.status === "stubbed" && " · Stub"}
            </div>
            {t.quota && (
              <div className="mt-3 text-[11px] font-mono space-y-0.5"
                   style={{ color: "var(--text-secondary)" }}>
                <div>Plan left: <span style={{ color: "var(--text-primary)" }}>{t.quota.plan_searches_left ?? "—"}</span></div>
                <div>Total left: <span style={{ color: "var(--text-primary)" }}>{t.quota.total_searches_left ?? "—"}</span></div>
                <div>Used this month: <span style={{ color: "var(--text-primary)" }}>{t.quota.this_month_usage ?? "—"}</span></div>
              </div>
            )}
            {t.note && (
              <div className="mt-2 text-[10px] italic" style={{ color: "var(--text-muted)" }}>{t.note}</div>
            )}
          </div>
        ))}
        <div className="saa-card p-4">
          <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--text-muted)" }}>
            Next scheduled run
          </div>
          <div className="font-mono text-sm font-semibold mt-2">
            {data?.next_scheduled_run ? new Date(data.next_scheduled_run).toLocaleString() : "—"}
          </div>
          {data?.last_job && (
            <>
              <div className="text-[10px] uppercase tracking-[0.18em] mt-4" style={{ color: "var(--text-muted)" }}>
                Last run
              </div>
              <div className="font-mono text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
                {timeAgo(data.last_job.finished_at || data.last_job.started_at)} · {data.last_job.fares_inserted} cells · {data.last_job.duration_seconds?.toFixed(1)}s
              </div>
            </>
          )}
        </div>
      </div>

      {/* Job history */}
      <div className="saa-card p-5">
        <div className="text-[10px] uppercase tracking-[0.18em] mb-3" style={{ color: "var(--text-muted)" }}>
          Recent fetch jobs
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ color: "var(--text-secondary)" }} className="text-[11px] uppercase tracking-[0.12em]">
              <th className="text-left pb-2">Started</th>
              <th className="text-left pb-2">Status</th>
              <th className="text-left pb-2">Routes</th>
              <th className="text-left pb-2">Cells</th>
              <th className="text-left pb-2">Duration</th>
              <th className="text-left pb-2">Trigger</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.id} style={{ borderTop: "1px solid var(--border-subtle)" }}>
                <td className="py-2 font-mono text-xs">{new Date(j.started_at).toLocaleString()}</td>
                <td className="py-2">
                  <span className={`pill ${j.status === "success" ? "pill-competitive" : j.status === "running" ? "pill-slight" : "pill-undercut"}`}>
                    {j.status}
                  </span>
                </td>
                <td className="py-2 font-mono">{j.routes_processed}</td>
                <td className="py-2 font-mono">{j.fares_inserted}</td>
                <td className="py-2 font-mono">{j.duration_seconds?.toFixed(1) || "—"}s</td>
                <td className="py-2 text-xs" style={{ color: "var(--text-secondary)" }}>{j.triggered_by}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
